import type { PromptResolve } from '@ur-solutions/react-components'
import { useForm, useTranslate } from '@ur-solutions/react-hooks'
import { Input, RegularModal } from '@/components_deprecated'
import { DetectBlur } from '@/containers/util-containers'
import {
  TAG_PRESET_COLORS,
  TagColorPicker,
} from '@/components/tag-color-picker'
import { Field, Form } from '..'

export interface IssueTagForm {
  name: string
  color: string
  description: string
}

interface ExistingTag {
  id: string
  name: string
}

interface IssueTagPromptProps {
  initialData?: IssueTagForm
  tagId?: string
  existingTags: ExistingTag[]
  onSubmit: PromptResolve<IssueTagForm | null>
}

export const IssueTagPrompt: React.FC<IssueTagPromptProps> = ({
  initialData,
  tagId,
  existingTags,
  onSubmit,
}) => {
  const translations = useTranslate({
    form: {
      label: {
        name: 'common.name',
        color: 'common.color',
        description: 'common.description',
      },
    },
    addTag: 'settings.add-issue-tag',
    editTag: 'settings.edit-issue-tag',
    create: 'common.create-alt',
    update: 'common.update',
    validation: {
      nameExists: 'settings.validation.issue-tag-name-exists',
      required: 'common.required',
    },
  })

  const editing = typeof initialData !== 'undefined'

  function validateName(name: string) {
    if (name === '') return translations.validation.required
    const collision = existingTags.find(t => t.name === name && t.id !== tagId)
    if (collision) return translations.validation.nameExists
    return null
  }

  const {
    formValues: form,
    formErrors: errors,
    formValid,
    formChangeHandler: handler,
    updateForm,
    submitHandler: submit,
  } = useForm<IssueTagForm>({
    values: initialData ?? {
      name: '',
      color: TAG_PRESET_COLORS[4]!,
      description: '',
    },
    validators: {
      name: val => validateName(val),
    },
    config: {
      initAsInvalid: true,
    },
  })

  return (
    <DetectBlur active onClickOutside={() => onSubmit(null)}>
      <RegularModal
        title={editing ? translations.editTag : translations.addTag}
        submitText={editing ? translations.update : translations.create}
        submitDisabled={!formValid}
        width="480px"
        onCancel={() => onSubmit(null)}
        onSubmit={submit(onSubmit)}
      >
        <Form>
          <Field>
            <label>{translations.form.label.name}</label>
            <Input
              value={form.name}
              error={errors.name}
              autoFocus
              fullWidth
              onChange={handler('name')}
            />
          </Field>
          <Field>
            <label>{translations.form.label.color}</label>
            <TagColorPicker
              value={form.color}
              onChange={color => updateForm({ color })}
            />
          </Field>
          <Field>
            <label>{translations.form.label.description}</label>
            <Input
              value={form.description}
              fullWidth
              onChange={handler('description')}
            />
          </Field>
        </Form>
      </RegularModal>
    </DetectBlur>
  )
}
